/*
 * BindTelemetrySetModal — bindet ein Telemetrieset an einen Regler
 * (Konzept §4.3/§4.4).
 *
 * Ablauf:
 *   1. GET /api/telemetry-sets            -> Auswahlliste
 *   2. GET .../telemetry-binding          -> was aktuell gebunden ist
 *   3. Set anklicken -> POST .../preview  -> Auflösungswarnungen + Lizenzstand
 *   4. Anwenden      -> PUT  .../telemetry-binding
 *
 * Der Vorschau-Schritt ist der Kern dieses Dialogs und kein Beiwerk. Zwei
 * Dinge müssen VOR dem Klick sichtbar sein:
 *
 *   - Nicht auflösbare Punkte. §4.4 sagt: sie werden als Warnung angezeigt,
 *     nicht stumm verworfen. Ein Template, das die Hälfte still schluckt,
 *     ist schlimmer als eines, das scheitert.
 *   - Die Datenpunktgrenze der Lizenz. Sie wird beim Speichern durchgesetzt;
 *     sie erst dort zu erfahren wäre eine vermeidbare Sackgasse. Reisst sie,
 *     bleibt der Anwenden-Knopf gesperrt und die Meldung nennt Limit und
 *     Anzahl.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { IconCancel } from '../icons/HvacIcons';
import { describeError, type TelemetrySetDetail } from './TelemetrySetEditorModal';
import './hv-modal.css';

/** Ein Set in der Auswahlliste. */
export interface TelemetrySetListItem {
  id: string;
  name: string;
  description: string | null;
  version: number;
  pointCount: number;
  boundControllerCount: number;
  staleControllerCount: number;
  updatedAt: string;
}

/** Eine Auflösungswarnung. `bound` sagt, ob der Punkt trotz Warnung gebunden
 *  wurde — ein Symbol-Rückfall ist ein Treffer mit Vorbehalt. */
export interface TelemetryResolutionWarning {
  datapointRef: string;
  kind: 'ByPath' | 'BySymbol' | 'Ambiguous' | 'NotFound' | 'Duplicate';
  bound: boolean;
  message: string;
}

/** Was das Anwenden bewirken würde bzw. bewirkt hat. */
export interface TelemetryBindingOutcome {
  telemetrySetId: string;
  telemetrySetName: string;
  setVersion: number;
  appliedVersion: number;
  pointCount: number;
  boundCount: number;
  unresolvedCount: number;
  warnings: TelemetryResolutionWarning[];
  recordedDatapointsBefore: number;
  recordedDatapointsAfter: number;
  datapointLimit: number;
  withinLicense: boolean;
  licenseMessage: string | null;
}

/** Ein Punkt-Override auf dem Draht. */
export interface TelemetryPointOverrideDto {
  datapointRef: string;
  covAbsolute: number | null;
  covPercent: number | null;
  minIntervalSec: number | null;
  heartbeatSec: number | null;
}

/** Die aktuelle Bindung eines Reglers. */
export interface ControllerTelemetryBindingDto {
  deviceId: string;
  telemetrySetId: string;
  telemetrySetName: string;
  appliedVersion: number;
  currentSetVersion: number;
  isStale: boolean;
  appliedAt: string;
  resolvedPointCount: number;
  unresolvedPointCount: number;
  unresolvedRefs: string[];
  overrides: TelemetryPointOverrideDto[];
}

interface Props {
  plantId: string;
  deviceId: string;
  deviceLabel: string;
  onApplied(outcome: TelemetryBindingOutcome): void;
  onRemoved(): void;
  /** Öffnet den Set-Editor für ein neues Set — der Weg raus, wenn noch keines
   *  existiert. */
  onCreateSet(): void;
  onClose(): void;
}

export function BindTelemetrySetModal({
  plantId, deviceId, deviceLabel, onApplied, onRemoved, onCreateSet, onClose,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<TelemetryBindingOutcome | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  /** Overrides der bestehenden Bindung. Beim Anwenden mitgeschickt, damit ein
   *  erneutes Anwenden sie nicht wegwirft — der Body ist vollständig. */
  const [keepOverrides, setKeepOverrides] = useState<TelemetryPointOverrideDto[]>([]);

  const setsState = useAsync(
    (signal) => api.get<TelemetrySetListItem[]>('/api/telemetry-sets', signal),
    [],
  );

  // 404 heißt hier „keine Bindung", nicht „Fehler" — deshalb abgefangen und
  // als null behandelt.
  const bindingState = useAsync(
    (signal) => api
      .get<ControllerTelemetryBindingDto>(
        `/api/plants/${plantId}/devices/${deviceId}/telemetry-binding`, signal)
      .catch(() => null),
    [plantId, deviceId],
  );

  const currentBinding = bindingState.status === 'ok' ? bindingState.data : null;

  // Das gebundene Set vorauswählen, damit „erneut anwenden" ein Klick ist.
  useEffect(() => {
    if (currentBinding && selectedId === null) {
      setSelectedId(currentBinding.telemetrySetId);
      setKeepOverrides(currentBinding.overrides);
    }
  }, [currentBinding, selectedId]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, busy]);

  // Vorschau nachziehen, sobald ein Set gewählt ist. Effekt und nicht
  // Klick-Handler, damit auch die Vorauswahl oben eine Vorschau bekommt.
  useEffect(() => {
    if (selectedId === null) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    setError(null);
    api.post<TelemetryBindingOutcome>(
      `/api/plants/${plantId}/devices/${deviceId}/telemetry-binding/preview`,
      { telemetrySetId: selectedId, overrides: keepOverrides },
    ).then((res) => {
      if (!cancelled) setPreview(res);
    }).catch((e: unknown) => {
      if (!cancelled) { setPreview(null); setError(describeError(e)); }
    }).finally(() => {
      if (!cancelled) setPreviewing(false);
    });
    return () => { cancelled = true; };
  }, [selectedId, plantId, deviceId, keepOverrides]);

  const sets = setsState.status === 'ok' ? setsState.data : [];
  const selected = useMemo(() => sets.find((s) => s.id === selectedId) ?? null, [sets, selectedId]);

  const canApply = selected !== null
    && preview !== null
    && preview.withinLicense
    && preview.boundCount > 0
    && !busy
    && !previewing;

  const apply = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.put<{ outcome: TelemetryBindingOutcome }>(
        `/api/plants/${plantId}/devices/${deviceId}/telemetry-binding`,
        { telemetrySetId: selected.id, overrides: keepOverrides },
      );
      onApplied(res.outcome);
      onClose();
    } catch (e) {
      setError(describeError(e));
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/plants/${plantId}/devices/${deviceId}/telemetry-binding`);
      onRemoved();
      onClose();
    } catch (e) {
      setError(describeError(e));
      setBusy(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        class="hv-modal hv-modal--bind-telemetry"
        style={{ maxWidth: 760 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Telemetrieset binden</h2>
          <button type="button" class="hv-modal__close" onClick={onClose}
                  aria-label="Schließen" disabled={busy}>×</button>
        </div>

        <div class="hv-modal__body">
          <p class="hv-modal__hint">
            Regler <code>{deviceLabel}</code>. Das gebundene Set legt fest, welche Datenpunkte
            dieser Regler aufzeichnet und mit welchen Schwellen.
          </p>

          {currentBinding && (
            <div class="hv-modal__field">
              <label>Aktuell gebunden</label>
              <p class="hv-modal__hint">
                <strong>{currentBinding.telemetrySetName}</strong>{' '}
                <span class="hv-modal__listbox-item-badge">v{currentBinding.appliedVersion}</span>
                {currentBinding.isStale && (
                  <>
                    {' '}
                    <span
                      class="hv-modal__listbox-item-badge"
                      title="Das Set wurde geändert, seit es hier angewandt wurde"
                      style={{ background: 'rgba(220,160,60,0.25)' }}
                    >
                      veraltet — Set ist bei v{currentBinding.currentSetVersion}
                    </span>
                  </>
                )}
                <br />
                {currentBinding.resolvedPointCount} Datenpunkte werden aufgezeichnet, angewandt
                am {formatDate(currentBinding.appliedAt)}.
                {currentBinding.overrides.length > 0
                  && ` ${currentBinding.overrides.length} punktweise Abweichung(en) — sie werden beim erneuten Anwenden beibehalten.`}
              </p>
              {currentBinding.unresolvedRefs.length > 0 && (
                <p class="hv-modal__error">
                  Beim letzten Anwenden nicht auflösbar:{' '}
                  {currentBinding.unresolvedRefs.map((r) => <code key={r}>{r} </code>)}
                </p>
              )}
            </div>
          )}

          <div class="hv-modal__field">
            <label>Telemetrieset</label>
            {setsState.status === 'loading' && <p class="hv-modal__hint">Lade Sets…</p>}
            {setsState.status === 'error' && (
              <p class="hv-modal__error">Telemetriesets konnten nicht geladen werden.</p>
            )}
            {setsState.status === 'ok' && sets.length === 0 && (
              <p class="hv-modal__hint">
                Es gibt noch kein Telemetrieset.{' '}
                <button type="button" class="hv-btn hv-btn--sm" onClick={onCreateSet}>
                  Jetzt eines anlegen
                </button>
              </p>
            )}
            {sets.length > 0 && (
              <div role="listbox" aria-label="Telemetriesets" class="hv-modal__listbox">
                {sets.map((s) => {
                  const isSel = s.id === selectedId;
                  return (
                    <div
                      key={s.id}
                      role="option"
                      aria-selected={isSel}
                      tabIndex={0}
                      class={`hv-modal__listbox-item${isSel ? ' hv-modal__listbox-item--active' : ''}`}
                      onClick={() => !busy && setSelectedId(s.id)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !busy) {
                          e.preventDefault();
                          setSelectedId(s.id);
                        }
                      }}
                    >
                      <div class="hv-modal__listbox-item-title">
                        <span class="hv-modal__listbox-item-name">{s.name}</span>
                        <span class="hv-modal__listbox-item-badge">v{s.version}</span>
                        <span class="hv-modal__listbox-item-badge">{s.pointCount} Punkte</span>
                      </div>
                      {s.description && (
                        <div class="hv-modal__listbox-item-desc">{s.description}</div>
                      )}
                      <div class="hv-modal__listbox-item-meta">
                        {s.boundControllerCount === 0
                          ? 'Auf keinem Regler gebunden'
                          : `Auf ${s.boundControllerCount} Regler(n) gebunden${
                              s.staleControllerCount > 0
                                ? `, ${s.staleControllerCount} davon veraltet`
                                : ''}`}
                        {' · '}Aktualisiert: {formatDate(s.updatedAt)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Auch neben vorhandenen Sets: sonst gab es nach dem ersten Set
                keinen Weg mehr in den Editor, und jeder weitere Regler musste
                ein fremdes Set nehmen. */}
            {sets.length > 0 && (
              <p class="hv-modal__hint">
                Kein passendes dabei?{' '}
                <button type="button" class="hv-btn hv-btn--sm" onClick={onCreateSet} disabled={busy}>
                  Neues Telemetrieset anlegen
                </button>
              </p>
            )}
          </div>

          {previewing && <p class="hv-modal__hint">Löse Datenpunkte auf…</p>}

          {preview && !previewing && (
            <div class="hv-modal__field">
              <label>Vorschau</label>
              <p class="hv-modal__hint">
                <strong>{preview.boundCount}</strong> von {preview.pointCount} Punkten sind auf
                diesem Regler auflösbar und würden gebunden.
                {preview.unresolvedCount > 0 && (
                  <> <strong>{preview.unresolvedCount}</strong> nicht — siehe unten.</>
                )}
                <br />
                Aufgezeichnete Datenpunkte: {preview.recordedDatapointsBefore}
                {' → '}
                <strong>{preview.recordedDatapointsAfter}</strong>
                {' '}von {preview.datapointLimit} erlaubten (Lizenzgrenze je Regler).
              </p>

              {/* Die Lizenzgrenze wird beim Speichern durchgesetzt. Sie hier
                  zu zeigen ist der Unterschied zwischen einer Meldung und
                  einer Sackgasse. */}
              {!preview.withinLicense && preview.licenseMessage && (
                <p class="hv-modal__error">{preview.licenseMessage}</p>
              )}

              {preview.withinLicense && preview.boundCount === 0 && (
                <p class="hv-modal__error">
                  Kein einziger Punkt dieses Sets ist auf diesem Regler auflösbar. Prüfen, ob das
                  Set zum Schema dieses Reglers passt.
                </p>
              )}

              {/* §4.4 wörtlich: nicht auflösbare Punkte werden ANGEZEIGT.
                  Auch die gebundenen mit Vorbehalt (Symbol-Rückfall) stehen
                  hier — sonst würde eine stille Pfadabweichung erst auffallen,
                  wenn die falsche Zeitreihe im Chart steht. */}
              {preview.warnings.length > 0 && (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
                  {preview.warnings.map((w) => (
                    <li
                      key={`${w.kind}:${w.datapointRef}`}
                      style={{ color: w.bound ? 'var(--warn, #d8a83c)' : 'var(--error, #f78787)' }}
                    >
                      <span style={{ opacity: 0.85 }}>{w.bound ? 'gebunden — ' : 'nicht gebunden — '}</span>
                      {w.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && <p class="hv-modal__error">{error}</p>}
        </div>

        <div class="hv-modal__footer">
          {currentBinding && !confirmRemove && (
            <button
              type="button"
              class="hv-btn hv-btn--sm hv-btn--secondary"
              style={{ color: '#f78787', borderColor: 'rgba(220,60,60,0.4)' }}
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              Bindung entfernen
            </button>
          )}
          {currentBinding && confirmRemove && (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-muted,#b0b0b0)' }}>
                Dieser Regler zeichnet danach nichts mehr auf. Bereits aufgezeichnete Daten
                bleiben; die Live-Anzeige läuft weiter.
              </span>
              <button
                type="button"
                class="hv-btn hv-btn--sm"
                style={{ background: '#c14040', color: '#fff', borderColor: '#c14040' }}
                disabled={busy}
                onClick={remove}
              >
                Ja, entfernen
              </button>
              <button type="button" class="hv-btn hv-btn--sm hv-btn--secondary" disabled={busy}
                      onClick={() => setConfirmRemove(false)}>
                Abbrechen
              </button>
            </>
          )}
          <div class="hv-modal__footer-spacer" />
          {!confirmRemove && (
            <>
              <button type="button" class="hv-btn" onClick={onClose} disabled={busy}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <IconCancel size={14} />Abbrechen
                </span>
              </button>
              <button
                type="button"
                class="hv-btn hv-btn--primary"
                onClick={apply}
                disabled={!canApply}
                title={
                  selected === null ? 'Zuerst ein Telemetrieset wählen'
                    : previewing ? 'Vorschau läuft'
                    : preview === null ? 'Vorschau fehlgeschlagen'
                    : !preview.withinLicense ? 'Datenpunktgrenze der Lizenz überschritten'
                    : preview.boundCount === 0 ? 'Kein Punkt ist auf diesem Regler auflösbar'
                    : undefined
                }
              >
                {busy ? 'Wende an…' : 'Anwenden'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Nur damit der Editor-Typ hier auftauchen darf, ohne dass der Import als
 *  unbenutzt gilt — der Bind-Dialog gibt einen frisch angelegten Set nicht
 *  selbst zurück, das macht PlantSchemaPage. */
export type { TelemetrySetDetail };

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
